import { noop } from "lodash-es";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tab from "ds/components/Tab";
import useTypedContext from "hooks/useTypedContext";
import useHasSpaceAdminAccess from "shared/Space/useHasSpaceAdminAccess";
import { Space } from "types/generated";
import { isLegacySpace } from "utils/space";
import { SpacesContext } from "views/Account/SpacesProvider";

import { showDeleteConfirmation } from "../../Spaces/DeleteConfirmation";
import { showSpaceDrawer } from "../../Spaces/SpacesDrawer";
import { SpacesDrawerWriteMode } from "../../Spaces/types";
import { isPredefinedSpace } from "../../Spaces/utils";

type SpaceDetailsHeaderProps = {
  space: Space;
  spaceUrl: string;
  isRootAdmin: boolean;
};

const SpaceDetailsHeader = ({ space, spaceUrl, isRootAdmin }: SpaceDetailsHeaderProps) => {
  const { spaces, canNonRootAdminCreateSpace } = useTypedContext(SpacesContext);

  const hasAccessToSpace = useHasSpaceAdminAccess(space.id);
  const hasAccessToParentSpace = useHasSpaceAdminAccess(space.parentSpace || undefined);

  const hasChildren = spaces.some((item) => item.parentSpace === space.id);

  const canAddChild = isRootAdmin || (canNonRootAdminCreateSpace && hasAccessToSpace);
  const canEdit = isRootAdmin || (canNonRootAdminCreateSpace && hasAccessToParentSpace);
  const canDelete =
    (isRootAdmin || (canNonRootAdminCreateSpace && hasAccessToParentSpace)) &&
    !isPredefinedSpace(space.id) &&
    !hasChildren;

  const showDetailsDrawer = () => {
    showSpaceDrawer({ space, isRootAdmin, onClose: noop, hideNotEditableBanner: true });
  };

  const showCreateChildDrawer = () => {
    showSpaceDrawer({
      space,
      drawerWriteMode: SpacesDrawerWriteMode.Create,
      isRootAdmin,
      onClose: noop,
    });
  };

  const showEditDrawer = () => {
    showSpaceDrawer({
      space,
      drawerWriteMode: SpacesDrawerWriteMode.Edit,
      isRootAdmin,
      onClose: noop,
    });
  };

  const handleDelete = () => {
    showDeleteConfirmation({ name: space.name, id: space.id });
  };

  return (
    <ViewHeader>
      <ViewHeaderWrapper direction="row" justify="between" fullWidth>
        <ViewHeaderWrapper direction="row" align="center">
          <ViewHeaderTitle>{space.name}</ViewHeaderTitle>
        </ViewHeaderWrapper>

        <ViewHeaderWrapper direction="row" align="end" shrink="0">
          <Button variant="secondary" onPress={showDetailsDrawer}>
            Details
          </Button>

          <DropdownMenuEllipsis tooltip="Space actions" dotsSize="medium">
            {canAddChild && !isLegacySpace(space.id) && (
              <DropdownMenuItem onAction={showCreateChildDrawer}>Add child space</DropdownMenuItem>
            )}

            {canEdit && !isPredefinedSpace(space.id) && (
              <DropdownMenuItem onAction={showEditDrawer}>Edit details</DropdownMenuItem>
            )}

            <CopyFieldDropdownMenuItem title="Copy ID" value={space.id} />

            {canDelete && (
              <DropdownMenuItem onAction={handleDelete} danger>
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuEllipsis>
        </ViewHeaderWrapper>
      </ViewHeaderWrapper>

      <ViewHeaderNavigation>
        <Tab end to={spaceUrl} label="Stacks" />
        <Tab to={`${spaceUrl}/contexts`} label="Contexts" />
        <Tab to={`${spaceUrl}/policies`} label="Policies" />
        <Tab to={`${spaceUrl}/users`} label="Users" />
        <Tab to={`${spaceUrl}/idp-groups`} label="IdP Groups" />
        <Tab to={`${spaceUrl}/api-keys`} label="API Keys" />
      </ViewHeaderNavigation>
    </ViewHeader>
  );
};

export default SpaceDetailsHeader;
